import { evaluateExpression } from "../parser/expression.js";
/**
 * Resolve a condition source to a concrete value.
 */
export function resolveSource(source, context) {
    switch (source.type) {
        case "field":
            return context.fields[source.name] ?? null;
        case "field_group":
            return resolveFieldGroupAggregate(source, context);
        case "expression":
            return evaluateExpression(source.formula, context);
        case "document":
            return context.document?.[source.property] ?? null;
        case "batch":
            return context.batch?.[source.property] ?? null;
        case "user": {
            // P8 — the acting operator. Null when no user is in context (system
            // paths). `roles` returns the array (for contains / is one of).
            const u = context.user;
            if (!u)
                return null;
            if (source.property === "id")
                return u.id ?? null;
            if (source.property === "teamId")
                return u.teamId ?? null;
            return u.roles ?? null; // roles
        }
        case "lookup":
            return resolveLookup(source, context);
        case "confidence": {
            const map = context.fieldConfidence;
            if (!map)
                return false;
            if (source.scope === "field") {
                const c = source.name != null ? map[source.name] : undefined;
                return typeof c === "number" && c < source.below;
            }
            // any_field
            return Object.values(map).some((c) => typeof c === "number" && c < source.below);
        }
        default:
            return null;
    }
}
/**
 * Resolve a condition value to a concrete value.
 */
export function resolveValue(value, context) {
    switch (value.type) {
        case "literal":
            return value.value;
        case "field":
            return context.fields[value.name] ?? null;
        case "list":
            return value.values;
        case "lookup": {
            // Return all values from a lookup table column as a list
            const table = context.lookupData?.[value.table];
            if (!table)
                return [];
            return table.entries.map((e) => e.data[value.column] ?? e.key);
        }
        case "constant":
            // P8 — a job-level named constant; null when undeclared / no constants.
            return context.constants?.[value.name] ?? null;
        case "expression":
            return evaluateExpression(value.formula, context);
        case "relative-date": {
            // UTC calendar date + N days, as ISO YYYY-MM-DD — comparable via the
            // operator layer's string fallback (see applyOperator greater_than).
            const ms = Date.now() + value.days * 86_400_000;
            return new Date(ms).toISOString().slice(0, 10);
        }
        default:
            return null;
    }
}
// --- Field group aggregation ---
function resolveFieldGroupAggregate(source, context) {
    const values = source.names.map((name) => context.fields[name] ?? null);
    switch (source.aggregate) {
        case "count_checked":
            // A field is "checked" if it's truthy: non-empty string, "true", true, non-zero
            return values.filter((v) => isTruthy(v)).length;
        case "count_filled":
            // A field is "filled" if it's not null/undefined/empty string
            return values.filter((v) => !isEmpty(v)).length;
        case "sum": {
            let total = 0;
            for (const v of values) {
                const n = typeof v === "number" ? v : Number(v);
                if (!isNaN(n))
                    total += n;
            }
            return total;
        }
        case "any":
            return values.some((v) => isTruthy(v));
        case "all":
            return values.every((v) => isTruthy(v));
        case "none":
            return values.every((v) => !isTruthy(v));
        default:
            return null;
    }
}
function isTruthy(val) {
    if (val === null || val === undefined)
        return false;
    if (typeof val === "string") {
        const trimmed = val.trim().toLowerCase();
        return trimmed !== "" && trimmed !== "false" && trimmed !== "0";
    }
    if (typeof val === "number")
        return val !== 0;
    if (typeof val === "boolean")
        return val;
    return Boolean(val);
}
function isEmpty(val) {
    if (val === null || val === undefined)
        return true;
    if (typeof val === "string")
        return val.trim() === "";
    return false;
}
// --- Lookup resolution ---
function resolveLookup(source, context) {
    const table = context.lookupData?.[source.table];
    if (!table)
        return null;
    // Resolve matchValue (may contain field reference like {vendorName})
    let matchVal = source.matchValue;
    const fieldRefMatch = matchVal.match(/^\{(\w+)\}$/);
    if (fieldRefMatch) {
        const fieldVal = context.fields[fieldRefMatch[1]];
        matchVal = fieldVal !== null && fieldVal !== undefined ? String(fieldVal) : "";
    }
    // Find matching entry
    const entry = table.entries.find((e) => e.key.toLowerCase() === matchVal.toLowerCase());
    if (!entry)
        return null;
    return entry.data[source.returnField] ?? null;
}
//# sourceMappingURL=resolvers.js.map